import {
  CraftsmanCostCalcOutput,
  MainPropertyType,
  MultiFamilyFeatures,
  isDefined,
  isUndefined,
} from '@aloft/toolkit-shared';
import { createSelector } from '@reduxjs/toolkit';

import { DetailFeatures } from '../../../constants.ts';
import { selectSubjectFinishedBasementAreaCostOutput } from '../costSelectors/selectFinishedBasementAreaCost.ts';
import { selectSubjectFullBathCostOutput } from '../costSelectors/selectFullBathCost.ts';
import { selectSubjectHalfBathCostOutput } from '../costSelectors/selectHalfBathCost.ts';
import { selectSubjectMainStructureCostOutput } from '../costSelectors/selectMainStructureCost.ts';
import { selectSubjectTotalBasementAreaCostOutput } from '../costSelectors/selectTotalBasementAreaCost.ts';
import { selectAdjustmentSettings, selectPropertyType } from '../orderSelectors.ts';
import { selectImprovementsEntities } from '../propertyDataSelectors/improvementsSelectors.ts';
import { selectSubjectDepreciationRatioForFeature } from '../selectDepreciationRatioForFeature.ts';
import { selectOrderFeatures } from '../selectOrderFeatures.ts';
import { costOutputToCostPerUnitString } from '../utils/costOutputToCostPerUnitString.ts';
import { getCommentaryTableHelperText } from '../utils/getCommentaryTableHelperText.ts';

const getMainDwellingItemObj = (
  title: string,
  costOutput?: CraftsmanCostCalcOutput,
  depreciationRatio?: number,
) => {
  if (isUndefined(costOutput)) {
    return {
      title,
      value: '',
      formula: '',
    };
  }

  return {
    title,
    value: costOutputToCostPerUnitString(costOutput, true, depreciationRatio) ?? '-',
    formula: isDefined(depreciationRatio)
      ? `${costOutput?.variable?.toString()} x ${((1 - depreciationRatio) * 100).toFixed(
          2,
        )}% = ${costOutputToCostPerUnitString(costOutput, false, depreciationRatio)}`
      : '-',
  };
};

const getRoundingAndThresholdsArr = (
  adjustmentSettings: ReturnType<typeof selectAdjustmentSettings>,
  orderFeatures: DetailFeatures[],
  propertyType: MainPropertyType,
): string[] => {
  const areaLabel = propertyType === 'multiFamily' ? 'GBA' : 'GLA';
  return [
    getCommentaryTableHelperText(adjustmentSettings, 'mainDwelling', areaLabel) ?? '',
    propertyType === 'manufacturedHome'
      ? (getCommentaryTableHelperText(adjustmentSettings, 'foundation', 'Foundation') ?? '')
      : '',
    propertyType === 'multiFamily'
      ? (getCommentaryTableHelperText(adjustmentSettings, 'units', 'Number of Units') ?? '')
      : '',
    getCommentaryTableHelperText(adjustmentSettings, 'bathAboveGrade', 'Bath') ?? '',
    ...(orderFeatures.includes('totalBasement')
      ? [getCommentaryTableHelperText(adjustmentSettings, 'basement', 'Basement') ?? '']
      : []),
    ...(orderFeatures.includes('belowGradeBath')
      ? [
          getCommentaryTableHelperText(
            adjustmentSettings,
            'bathBelowGrade',
            'Bath (Below Grade)',
          ) ?? '',
        ]
      : []),
  ].filter((text) => text.length > 0);
};

export const selectMainDwellingCostsCommentaryDisplay = createSelector(
  [
    (state) => selectSubjectDepreciationRatioForFeature(state, undefined),
    selectSubjectMainStructureCostOutput,
    selectSubjectFullBathCostOutput,
    selectSubjectHalfBathCostOutput,
    selectSubjectTotalBasementAreaCostOutput,
    selectSubjectFinishedBasementAreaCostOutput,
    selectOrderFeatures,
    selectPropertyType,
    selectAdjustmentSettings,
    selectImprovementsEntities,
  ],
  (
    depreciationRatio,
    subjectCostOutput,
    fullBathCostOutput,
    halfBathCostOutput,
    basementTotalCostOutput,
    basementFinishedCostOutput,
    orderFeatures,
    propertyType,
    adjustmentSettings,
    improvements,
  ) => ({
    gla: getMainDwellingItemObj(
      propertyType === 'multiFamily' ? 'GBA' : 'GLA',
      subjectCostOutput,
      depreciationRatio,
    ),
    ...(propertyType === 'multiFamily' &&
      getDepreciatedMultifamilyCost({
        subjectCostOutput,
        depreciationRatio,
        subjectNumberOfUnits: (improvements.subject as MultiFamilyFeatures | undefined)
          ?.numberOfUnits,
      })),
    fullBath: getMainDwellingItemObj('Full Bath', fullBathCostOutput, depreciationRatio),
    halfBath: getMainDwellingItemObj('Half Bath', halfBathCostOutput, depreciationRatio),
    ...(orderFeatures.includes('totalBasement') && {
      basementTotal: getMainDwellingItemObj(
        'Basement Total',
        basementTotalCostOutput,
        depreciationRatio,
      ),
    }),
    ...(orderFeatures.includes('finishedBasement') && {
      basementFinished: getMainDwellingItemObj(
        'Basement Finished',
        basementFinishedCostOutput,
        depreciationRatio,
      ),
    }),
    roundingAndThresholds: getRoundingAndThresholdsArr(
      adjustmentSettings,
      orderFeatures,
      propertyType ?? 'singleFamilyResidence',
    ),
  }),
);

function getDepreciatedMultifamilyCost({
  subjectCostOutput,
  subjectNumberOfUnits,
  depreciationRatio,
}: {
  subjectCostOutput?: CraftsmanCostCalcOutput;
  subjectNumberOfUnits?: number;
  depreciationRatio?: number;
}) {
  if (
    isUndefined(subjectCostOutput) ||
    isUndefined(depreciationRatio) ||
    isUndefined(subjectNumberOfUnits)
  ) {
    return {
      units: {
        title: 'Cost per additional unit',
        value: '-',
        formula: '-',
      },
    };
  }

  const subjectCostPerUnit = subjectCostOutput.fixed?.div(subjectNumberOfUnits);

  const depreciatedFixedCostPerUnit = subjectCostPerUnit?.mul(1 - depreciationRatio);

  return {
    units: {
      title: 'Cost per additional unit',
      value: depreciatedFixedCostPerUnit?.toString(true) ?? '-',
      formula: `${subjectCostPerUnit?.toString(true)} x ${((1 - depreciationRatio) * 100).toFixed(
        1,
      )}% = ${depreciatedFixedCostPerUnit?.toString(true)}`,
    },
  };
}
